import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { delftBlue, flame } from "@/theme/colors";
import FormStatsCards from "./components/FormStatsCards";
import FormTable from "./components/FormTable";
import ActivitiesPagination from "./components/ActivitiesPagination";
import CollapsibleSection from "./components/CollapsibleSection";
import { useGetPrestartFormsQuery } from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const FormsPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { 
    data: formsResponse, 
    isLoading: isLoadingForms, 
    error: formsError 
  } = useGetPrestartFormsQuery({ page, limit });

  const formsData = formsResponse?.data || {
    prestartForms: [],
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: limit,
    },
  };

  // Calculate stats from forms data
  const statsData = {
    total: formsData.pagination?.totalItems || 0,
    activitiesForms: formsData.prestartForms?.length || 0,
    otherForms: 0,
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleView = (form) => {
    navigate(`/forms/${form._id || form.id}`);
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Forms
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage shared activity form templates
            </Typography>
          </Box>
          {isSuperAdmin && (
            <Button
              onClick={() => navigate("/forms/add")}
              sx={{
                background: flame[500],
                color: "white",
                textTransform: "none",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 500,
                paddingX: 2,
                paddingY: 1,
                borderRadius: 1.5,
                "&:hover": { background: flame[600] },
              }}
            >
              Add Template
            </Button>
          )}
        </Box>

        {/* Stats Cards */}
        <CollapsibleSection
          title="Form Statistics"
          defaultExpanded={true}
          showCard={false}
        >
          <FormStatsCards stats={statsData} />
        </CollapsibleSection>

        {/* Forms Table */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          {isLoadingForms ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
              <CircularProgress />
            </Box>
          ) : formsError ? (
            <Alert severity="error">
              {formsError?.data?.message || "Failed to load forms"}
            </Alert>
          ) : (
            <>
              <FormTable
                forms={formsData.prestartForms || []}
                onView={handleView}
                isSuperAdmin={isSuperAdmin}
              />

              {/* Pagination */}
              <ActivitiesPagination
                page={formsData.pagination?.currentPage || page}
                limit={formsData.pagination?.itemsPerPage || limit}
                total={formsData.pagination?.totalItems || 0}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default FormsPage;



